import { useEffect, useRef } from 'react';
import { JournalText, Search } from 'react-bootstrap-icons';

import { Input, Space } from 'antd';

import EmptyState from '@/components/shared/EmptyState';
import HorizontalList from '@/components/shared/HorizontalList';
import PageTitle from '@/components/shared/PageTitle';
import Wrapper from '@/components/shared/Wrapper';
import { useDebounce } from '@/hooks/useDebounce';
import { useWindowScrollReachedEnd } from '@/hooks/useWindowScrollReachedEnd';
import { IPaginationParams } from '@/interfaces/pagination';
import { notesApi } from '@/services/notes';

import Card from './Card';
import { NotesContextProvider } from './context';
import NotesPreview from './Preview';

export type FiltersNotes = {
  page?: number;
  size?: number;
  search?: string;
  order?: 'ASC' | 'DESC';
};

const Notes = () => {
  const filters = useRef<Partial<IPaginationParams>>({ page: 1 });
  const { debounce } = useDebounce();
  const [refetch, { data: notes, isFetching }] = notesApi.endpoints.getAllNotations.useLazyQuery();
  const reachedEnd = useWindowScrollReachedEnd();
  const hasNotes = notes?.data.length > 0;

  const handleSearch = (event: React.ChangeEvent<{ value: string }>) => {
    debounce(() => {
      refetch({ search: event.target.value || undefined });
    }, 800);
  };

  useEffect(() => {
    if (!notes || isFetching || !reachedEnd) return;

    if (notes.total_pages > notes.page.page) {
      refetch({
        ...notes.page,
        page: (notes?.page.page ?? 0) + 1
      });
    }
  }, [reachedEnd, notes, isFetching, refetch]);

  useEffect(() => {
    refetch(filters.current);
  }, [refetch]);

  return (
    <Wrapper>
      <NotesContextProvider>
        <PageTitle
          title='Minhas Anotações'
          endContent={
            <Space size={16}>
              <Input
                size='large'
                placeholder='Buscar'
                prefix={<Search size={12} />}
                allowClear
                onChange={handleSearch}
              />
            </Space>
          }
        />
        {!isFetching && !hasNotes && (
          <EmptyState
            icon={<JournalText />}
            title='Suas anotações aparecerão aqui.'
            description='Aqui você poderá acessar com facilidade todas as anotações feitas nas aulas.'
          />
        )}

        <HorizontalList
          isFetching={isFetching}
          isPaginated={true}
          pagination={{
            totalRows: notes?.total_rows,
            totalPages: notes?.total_pages,
            pageSize: notes?.page.size,
            page: notes?.page.page
          }}
        >
          {notes?.data.map((note, key) => (
            <Card {...note} key={`notation-${key}`} />
          ))}
        </HorizontalList>
        <NotesPreview />
      </NotesContextProvider>
    </Wrapper>
  );
};

export default Notes;
